import React, { useState, useEffect } from 'react';
import {
  CheckCircle, Eye, Mail, Phone, Briefcase,
  Calendar, DollarSign, Clock, AlertCircle, Building2,
  ArrowLeft, CheckSquare, Loader, Globe,
  X, Plus, Trash2, UserCheck, Users, Tag, FileText, ExternalLink, Video,
  MapPin, GraduationCap
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { candidatesAPI } from "../../services/api";
import { toast } from "react-toastify";

const STATUS_CONFIG = {
  Shortlisted:  { label: 'Shortlisted',   color: 'bg-blue-100 text-blue-800 border-blue-200',          dot: 'bg-blue-500',    requiresTimeslots: false, requiresGivenCTC: false, requiresReason: false },
  Slotassigned: { label: 'Slot Assigned', color: 'bg-indigo-100 text-indigo-800 border-indigo-200',    dot: 'bg-indigo-500',  requiresTimeslots: true,  requiresGivenCTC: false, requiresReason: false },
  Selected:     { label: 'Selected / Hired', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500', requiresTimeslots: false, requiresGivenCTC: true,  requiresReason: false },
  Rejected:     { label: 'Rejected',      color: 'bg-red-100 text-red-800 border-red-200',             dot: 'bg-red-500',     requiresTimeslots: false, requiresGivenCTC: false, requiresReason: true  },
  'On Hold':    { label: 'On Hold',       color: 'bg-amber-100 text-amber-800 border-amber-200',       dot: 'bg-amber-500',   requiresTimeslots: false, requiresGivenCTC: false, requiresReason: true  },
};

const BADGE_CONFIG = {
  ...STATUS_CONFIG,
  'Interview Scheduled': { color: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
  Interview_Scheduled:   { color: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
  Screening:             { color: 'bg-purple-100 text-purple-800 border-purple-200', dot: 'bg-purple-500' },
};

const ALL_STATUSES = Object.keys(STATUS_CONFIG);

const COMMON_TIMEZONES = [
  'Asia/Kolkata','Asia/Tokyo','Asia/Singapore','Asia/Dubai',
  'Europe/London','Europe/Paris','Europe/Berlin',
  'America/New_York','America/Los_Angeles','America/Chicago',
  'Australia/Sydney',
];

// ── StatusBadge ───────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = BADGE_CONFIG[status] || { color: 'bg-gray-100 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {status?.replace(/_/g, ' ') || 'Unknown'}
    </span>
  );
};

// ── ResumeBadge ───────────────────────────────────────────────────────────────
const ResumeBadge = ({ resumeUrl }) => {
  if (!resumeUrl) return (
    <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-400 text-xs rounded-md">
      <FileText size={11} /> Not uploaded
    </span>
  );
  return (
    <button
      onClick={() => window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(resumeUrl)}&embedded=false`, '_blank', 'noopener,noreferrer')}
      className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium rounded-md hover:bg-emerald-100 transition-colors"
    >
      <ExternalLink size={11} /> View Resume
    </button>
  );
};

// ── TimeslotsInput ────────────────────────────────────────────────────────────
// datetime-local gives "2025-07-01T10:00"
// Backend expects "2025-07-01 10:00:00"
const TimeslotsInput = ({ timeslots, onChange }) => {
  const toBackendFmt = (v) => {
    if (!v) return '';
    const base = v.replace('T', ' ');          // "2025-07-01 10:00"
    return base.length === 16 ? base + ':00' : base; // append seconds if missing
  };
  const toInputFmt = (v) => {
    if (!v) return '';
    return v.replace(' ', 'T').slice(0, 16);   // "2025-07-01T10:00"
  };

  const addSlot    = () => onChange([...timeslots, '']);
  const removeSlot = (i) => onChange(timeslots.filter((_, idx) => idx !== i));
  const updateSlot = (i, val) => {
    const updated = [...timeslots];
    updated[i] = toBackendFmt(val);
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
        <Calendar size={13} /> Interview Time Slots <span className="text-red-500">*</span>
      </label>
      {timeslots.map((slot, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="datetime-local"
            value={toInputFmt(slot)}
            onChange={(e) => updateSlot(i, e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
          />
          {timeslots.length > 1 && (
            <button onClick={() => removeSlot(i)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
      <button onClick={addSlot} className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium mt-1">
        <Plus size={13} /> Add another slot
      </button>
    </div>
  );
};

// ── CandidateDetailModal ──────────────────────────────────────────────────────
const CandidateDetailModal = ({ candidate, onClose }) => {
  if (!candidate) return null;
  const formatDate = (d) => {
    if (!d) return 'N/A';
    try { return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return 'N/A'; }
  };
  const isInterviewScheduled =
    candidate.status === 'Interview Scheduled' || candidate.status === 'Interview_Scheduled';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg flex-shrink-0">
              {candidate.firstName?.[0]}{candidate.lastName?.[0]}
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{candidate.firstName} {candidate.lastName}</h2>
              <p className="text-sm text-slate-500 mt-0.5">{candidate.currentJobTitle || 'N/A'} {candidate.currentCompany ? `· ${candidate.currentCompany}` : ''}</p>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <StatusBadge status={candidate.status} />
                {candidate.applicationId && <span className="text-xs text-slate-400">{candidate.applicationId}</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 flex-shrink-0 ml-4"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5 space-y-6">
          <div>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl"><Mail size={14} className="text-slate-400 flex-shrink-0" /><span className="text-sm text-slate-700 truncate">{candidate.primaryEmail || 'N/A'}</span></div>
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl"><Phone size={14} className="text-slate-400 flex-shrink-0" /><span className="text-sm text-slate-700">{candidate.primaryPhone || 'N/A'}</span></div>
              {(candidate.city || candidate.country) && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl"><MapPin size={14} className="text-slate-400 flex-shrink-0" /><span className="text-sm text-slate-700">{[candidate.city, candidate.country].filter(Boolean).join(', ')}</span></div>
              )}
              {candidate.linkedInProfile && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl"><ExternalLink size={14} className="text-indigo-500 flex-shrink-0" /><a href={candidate.linkedInProfile} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline truncate">LinkedIn Profile</a></div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Professional</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'Experience',    value: candidate.totalYearsOfExperience ? `${candidate.totalYearsOfExperience} yrs` : 'N/A' },
                { label: 'Current CTC',   value: candidate.currentCTC  ? `₹${Number(candidate.currentCTC).toLocaleString('en-IN')}` : 'N/A' },
                { label: 'Expected CTC',  value: candidate.expectedCTC ? `₹${Number(candidate.expectedCTC).toLocaleString('en-IN')}` : 'N/A' },
                { label: 'Notice Period', value: candidate.noticePeriod || 'N/A' },
                ...(candidate.givenCTC         ? [{ label: 'Offered CTC',  value: `₹${Number(candidate.givenCTC).toLocaleString('en-IN')}` }] : []),
                ...(candidate.actualjoiningdate? [{ label: 'Joining Date', value: candidate.actualjoiningdate }] : []),
              ].map(({ label, value }) => (
                <div key={label} className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-slate-400 mb-0.5">{label}</p>
                  <p className="text-sm font-semibold text-slate-800">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {candidate.technicalSkills?.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Technical Skills</h3>
              <div className="flex flex-wrap gap-1.5">
                {candidate.technicalSkills.map((skill, idx) => (
                  <span key={idx} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-100">
                    {typeof skill === 'object' ? skill.skillName : skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Resume / CV</h3>
            {candidate.resumeUrl ? (
              <a href={`https://docs.google.com/viewer?url=${encodeURIComponent(candidate.resumeUrl)}&embedded=false`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors">
                <ExternalLink size={14} /> Open Resume
              </a>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-400 text-sm rounded-xl"><FileText size={13} /> No resume uploaded</span>
            )}
          </div>

          {!isInterviewScheduled && candidate.timeslots?.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                Interview Time Slots {candidate.clientTimeZone ? `(${candidate.clientTimeZone})` : ''}
              </h3>
              <div className="flex flex-wrap gap-2">
                {candidate.timeslots.map((slot, i) => (
                  <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border ${
                    candidate.selectedSlot === slot ? 'bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold' : 'bg-blue-50 text-blue-700 border-blue-200'
                  }`}>
                    <Calendar size={11} className="flex-shrink-0" />{slot}
                    {candidate.selectedSlot === slot && <CheckCircle size={11} className="flex-shrink-0" />}
                  </span>
                ))}
              </div>
            </div>
          )}

          {candidate.meetLink && (
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Meet Link</h3>
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                <Video size={16} className="text-blue-600 flex-shrink-0" />
                <a href={candidate.meetLink} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline font-medium break-all flex-1">{candidate.meetLink}</a>
                <button onClick={() => { navigator.clipboard.writeText(candidate.meetLink); toast.success('Meet link copied!'); }}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2.5 py-1.5 bg-white rounded-lg border border-blue-200 flex-shrink-0">Copy</button>
              </div>
            </div>
          )}

          {candidate.reason && (
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Reason</h3>
              <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-800">{candidate.reason}</div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0 flex justify-end bg-white rounded-b-2xl">
          <button onClick={onClose} className="px-5 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50">Close</button>
        </div>
      </div>
    </div>
  );
};

// ── InterviewSchedulingModal ──────────────────────────────────────────────────
// Step 1 → Pick timezone → calls getConvertedSlotsForCandidate (existing API ✅)
// Step 2 → Pick a converted slot
// Step 3 → Confirm email → calls scheduleInterview (existing API ✅)
const InterviewSchedulingModal = ({ candidate, token, onClose, onSuccess }) => {
  const [step, setStep]                     = useState(1);
  const [userTimeZone, setUserTimeZone]     = useState('Asia/Kolkata');
  const [convertedSlots, setConvertedSlots] = useState([]);
  const [selectedSlot, setSelectedSlot]     = useState('');
  const [toEmail, setToEmail]               = useState(candidate?.primaryEmail || '');
  const [bccEmail, setBccEmail]             = useState('');
  const [converting, setConverting]         = useState(false);
  const [scheduling, setScheduling]         = useState(false);

  // ── Step 1: fetch converted slots ──────────────────────────────────────
  const handleConvertSlots = async () => {
    if (!userTimeZone) { toast.error('Please select your timezone'); return; }
    setConverting(true);
    try {
      // ✅ candidatesAPI.getConvertedSlotsForCandidate exists in api.js
      // GET /api/interviews/{candidateApplicationId}/converted-slots?userTimeZone=...
      const res = await candidatesAPI.getConvertedSlotsForCandidate(candidate.id, userTimeZone);
      const slots = res.data?.convertedSlots || res.data?.slots || [];
      if (slots.length === 0) {
        toast.warning('No slots available. Client must assign slots with "Slotassigned" status first.');
        return;
      }
      setConvertedSlots(slots);
      // Pre-select existing selectedSlot if present in converted list
      if (candidate.selectedSlot && slots.includes(candidate.selectedSlot)) {
        setSelectedSlot(candidate.selectedSlot);
      } else {
        setSelectedSlot(slots[0]); // default to first
      }
      setStep(2);
    } catch (err) {
      toast.error('Slot conversion failed: ' + (err.response?.data?.message || err.message));
    } finally { setConverting(false); }
  };

  // ── Step 3: schedule interview ──────────────────────────────────────────
  const handleSchedule = async () => {
    if (!toEmail.trim()) { toast.error('Candidate email is required'); return; }
    setScheduling(true);
    try {
      // ✅ candidatesAPI.scheduleInterview exists in api.js
      // POST /api/interviews/schedule  with X-User-Id header
      const payload = {
        candidateApplicationId: candidate.id,
        selectedSlot,
        userTimeZone,
        jobId:         candidate.jobId || '',
        toEmail:       toEmail.trim(),
        candidateName: `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim(),
        ...(bccEmail.trim() ? { bccEmail: bccEmail.trim() } : {}),
      };
      await candidatesAPI.scheduleInterview(payload);
      toast.success('Interview scheduled! Meet link sent to candidate.');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error('Scheduling failed: ' + (err.response?.data?.message || err.message));
    } finally { setScheduling(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Schedule Interview</h2>
            <p className="text-sm text-slate-500 mt-0.5">{candidate.firstName} {candidate.lastName} · {candidate.primaryEmail}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>

        {/* Step indicator */}
        <div className="px-6 pt-4 flex items-center gap-2">
          {['Select Timezone', 'Pick Slot', 'Confirm'].map((label, idx) => (
            <React.Fragment key={idx}>
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${step === idx + 1 ? 'text-indigo-600' : step > idx + 1 ? 'text-emerald-600' : 'text-slate-400'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === idx + 1 ? 'bg-indigo-600 text-white' : step > idx + 1 ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {step > idx + 1 ? '✓' : idx + 1}
                </span>
                <span className="hidden sm:inline">{label}</span>
              </div>
              {idx < 2 && <div className="flex-1 h-px bg-slate-200" />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[50vh] overflow-y-auto">

          {/* ── Step 1 ── */}
          {step === 1 && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-blue-900">
                  Client assigned {candidate.timeslots?.length || 0} slot(s)
                  {candidate.clientTimeZone ? ` in ${candidate.clientTimeZone}` : ''}
                </p>
                <p className="text-xs text-blue-600 mt-1">Select your timezone to see slots converted to your local time.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-2 block">Your Timezone *</label>
                <select
                  value={userTimeZone}
                  onChange={(e) => setUserTimeZone(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  {COMMON_TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
            </>
          )}

          {/* ── Step 2 ── */}
          {step === 2 && (
            <>
              <p className="text-xs text-slate-500">All times shown in <b>{userTimeZone}</b>. Select one slot:</p>
              {candidate.selectedSlot && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800">
                  Previously selected: <b>{candidate.selectedSlot}</b>
                </div>
              )}
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {convertedSlots.map((slot, i) => (
                  <label key={i} className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-colors ${selectedSlot === slot ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-200'}`}>
                    <input type="radio" name="slot" value={slot} checked={selectedSlot === slot} onChange={() => setSelectedSlot(slot)} className="text-indigo-600" />
                    <span className="text-sm font-semibold text-slate-800">{slot}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          {/* ── Step 3 ── */}
          {step === 3 && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-1">Selected slot</p>
                <p className="text-sm font-bold text-emerald-800">{selectedSlot}</p>
                <p className="text-xs text-emerald-600">{userTimeZone}</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Candidate Email *</label>
                <input
                  type="email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="candidate@example.com"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">BCC Email <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  type="email"
                  value={bccEmail}
                  onChange={(e) => setBccEmail(e.target.value)}
                  placeholder="recruiter@example.com"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer buttons */}
        <div className="px-6 pb-6 pt-2 flex gap-3 border-t border-slate-100">
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} className="flex-1 border border-slate-200 text-slate-600 rounded-xl py-2.5 text-sm font-medium hover:bg-slate-50">
              Back
            </button>
          )}
          <button
            disabled={converting || scheduling}
            onClick={step === 1 ? handleConvertSlots : step === 2 ? () => { if (!selectedSlot) { toast.error('Please select a slot'); return; } setStep(3); } : handleSchedule}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {(converting || scheduling) && <Loader size={14} className="animate-spin" />}
            {step === 1 ? (converting ? 'Converting...' : 'Get Converted Slots') :
             step === 2 ? 'Confirm Slot' :
             (scheduling ? 'Scheduling...' : 'Schedule & Send Email')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── StatusUpdateModal ─────────────────────────────────────────────────────────
const StatusUpdateModal = ({ mode, candidateName, count, onConfirm, onClose, submitting }) => {
  const [status, setStatus]                 = useState('Shortlisted');
  const [givenCTC, setGivenCTC]             = useState('');
  const [joiningDate, setJoiningDate]       = useState('');
  const [timeslots, setTimeslots]           = useState(['']);
  const [clientTimeZone, setClientTimeZone] = useState('Asia/Kolkata');
  const [reason, setReason]                 = useState('');
  const cfg = STATUS_CONFIG[status] || {};

  const handleStatusChange = (s) => {
    setStatus(s); setGivenCTC(''); setJoiningDate(''); setTimeslots(['']); setReason('');
  };

  const handleConfirm = () => {
    if (cfg.requiresGivenCTC && !givenCTC.trim())                              { toast.error('Please enter the offered CTC'); return; }
    if (cfg.requiresTimeslots && timeslots.filter(s => s.trim()).length === 0) { toast.error('Please add at least one interview time slot'); return; }
    if (cfg.requiresTimeslots && !clientTimeZone.trim())                       { toast.error('Please select client timezone'); return; }
    if (cfg.requiresReason && !reason.trim())                                  { toast.error('Please provide a reason'); return; }

    const payload = { status };
    if (reason.trim())                             payload.reason            = reason.trim();
    if (cfg.requiresGivenCTC && givenCTC.trim())   payload.givenCTC          = givenCTC.trim();
    if (cfg.requiresTimeslots) {
      payload.timeslots      = timeslots.filter(s => s.trim());
      payload.clientTimeZone = clientTimeZone;
    }
    if (status === 'Selected' && joiningDate.trim()) payload.actualjoiningdate = joiningDate.trim();
    onConfirm(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {mode === 'bulk'
                  ? <><Users size={18} className="text-indigo-600" /><span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Bulk Update</span></>
                  : <><UserCheck size={18} className="text-indigo-600" /><span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Update Status</span></>}
              </div>
              <h2 className="text-lg font-bold text-slate-900">
                {mode === 'bulk' ? `Update ${count} candidate${count > 1 ? 's' : ''}` : candidateName}
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
              <Tag size={13} /> New Status <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {ALL_STATUSES.map(s => {
                const c = STATUS_CONFIG[s]; const isSelected = status === s;
                return (
                  <button key={s} onClick={() => handleStatusChange(s)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all text-left ${isSelected ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm' : 'border-slate-200 hover:border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`} />
                    <span className="truncate">{s === 'Selected' ? 'Selected / Hired' : s}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {cfg.requiresTimeslots && (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <Globe size={13} /> Client Timezone <span className="text-red-500">*</span>
                </label>
                <select value={clientTimeZone} onChange={e => setClientTimeZone(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white">
                  {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-1">Timezone in which the slots below are defined</p>
              </div>
              <TimeslotsInput timeslots={timeslots} onChange={setTimeslots} />
            </>
          )}

          {cfg.requiresGivenCTC && (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <DollarSign size={13} /> Offered CTC <span className="text-red-500">*</span>
                </label>
                <input type="text" placeholder="e.g. 1200000" value={givenCTC} onChange={e => setGivenCTC(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                  <Calendar size={13} /> Joining Date <span className="text-xs font-normal text-slate-400 normal-case ml-1">(optional)</span>
                </label>
                <input type="date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white" />
              </div>
            </>
          )}

          {(cfg.requiresReason || (!cfg.requiresTimeslots && !cfg.requiresGivenCTC && status !== 'Shortlisted')) && (
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <AlertCircle size={13} />
                {cfg.requiresReason ? <>Reason <span className="text-red-500">*</span></> : 'Reason (optional)'}
              </label>
              <textarea rows={3} placeholder={cfg.requiresReason ? 'Please provide a reason…' : 'Add a note (optional)'}
                value={reason} onChange={e => setReason(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white resize-none" />
            </div>
          )}
        </div>

        <div className="px-6 pb-6 flex items-center gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
          <button onClick={handleConfirm} disabled={submitting}
            className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? <><Loader size={15} className="animate-spin" /> Updating…</> : <><CheckCircle size={15} /> Confirm</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const ClientAccessPage = () => {
  const { token } = useParams();
  const navigate  = useNavigate();

  const [loading, setLoading]                       = useState(true);
  const [linkData, setLinkData]                     = useState(null);
  const [candidates, setCandidates]                 = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [submitting, setSubmitting]                 = useState(false);
  const [linkExpired, setLinkExpired]               = useState(false);
  const [modal, setModal]                           = useState(null);
  const [interviewModal, setInterviewModal]         = useState(null);
  const [detailCandidate, setDetailCandidate]       = useState(null);

  useEffect(() => { if (token) validateAndFetchData(); }, [token]);

  const validateAndFetchData = async () => {
    setLoading(true);
    try {
      await candidatesAPI.verifyToken(token);
      const response = await candidatesAPI.getApplicationsFromToken(token);
      if (response.data) {
        const candidatesList = response.data.applications || [];
        setCandidates(candidatesList);
        setLinkData({
          jobId:       response.data.jobId || 'N/A',
          expiresAt:   response.data.expiresAt || null,
          accessCount: response.data.accessCount || 0,
        });
        if (candidatesList.length > 0) toast.success(`${candidatesList.length} candidate(s) loaded`);
        else toast.info('No candidates found in this link');
      }
    } catch (error) {
      if ([401, 404, 410].includes(error.response?.status)) {
        setLinkExpired(true);
        toast.error('This link has expired or is invalid');
      } else {
        toast.error('Failed to load: ' + (error.response?.data?.message || error.message));
      }
    } finally { setLoading(false); }
  };

  const toggleSelect    = (id) => setSelectedCandidates(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedCandidates(selectedCandidates.length === candidates.length ? [] : candidates.map(c => c.id));

  const executeStatusUpdate = async (ids, payload) => {
    setSubmitting(true);
    try {
      await Promise.all(ids.map(applicationId =>
        candidatesAPI.updateStatusByToken(token, applicationId, payload)
      ));
      setCandidates(prev =>
        prev.map(c => ids.includes(c.id) ? {
          ...c,
          status: payload.status,
          ...(payload.givenCTC          ? { givenCTC: payload.givenCTC }                   : {}),
          ...(payload.timeslots         ? { timeslots: payload.timeslots }                 : {}),
          ...(payload.clientTimeZone    ? { clientTimeZone: payload.clientTimeZone }       : {}),
          ...(payload.reason            ? { reason: payload.reason }                       : {}),
          ...(payload.actualjoiningdate ? { actualjoiningdate: payload.actualjoiningdate } : {}),
        } : c)
      );
      toast.success(ids.length === 1
        ? `Updated to "${payload.status}"`
        : `${ids.length} candidates updated to "${payload.status}"`);
      setSelectedCandidates([]);
      setModal(null);
    } catch (error) {
      toast.error('Update failed: ' + (error.response?.data?.message || error.message));
    } finally { setSubmitting(false); }
  };

  const openSingleModal    = (candidate) => setModal({ mode: 'single', candidateId: candidate.id, candidateName: `${candidate.firstName} ${candidate.lastName}` });
  const openBulkModal      = () => { if (!selectedCandidates.length) { toast.error('Select at least one candidate'); return; } setModal({ mode: 'bulk', count: selectedCandidates.length }); };
  const handleModalConfirm = (payload) => { const ids = modal.mode === 'bulk' ? selectedCandidates : [modal.candidateId]; executeStatusUpdate(ids, payload); };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    try { return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return 'N/A'; }
  };

  // Show "Schedule Meet" button when:
  // status is Slotassigned AND no meetLink yet
  const canScheduleMeet = (c) =>
    (c.status === 'Slotassigned' || c.status === 'Interview Scheduled' || c.status === 'Interview_Scheduled')
    && !c.meetLink;

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-indigo-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-200">
          <Loader size={28} className="text-white animate-spin" />
        </div>
        <p className="text-base font-medium text-slate-600">Loading candidates…</p>
      </div>
    </div>
  );

  if (linkExpired) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-10 max-w-sm w-full text-center border border-slate-100">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
          <AlertCircle size={32} className="text-red-500" />
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Link Expired</h1>
        <p className="text-sm text-slate-500 mb-6">This candidate sharing link is no longer valid. Contact the recruiter for a new one.</p>
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors">
          <ArrowLeft size={16} /> Go to Home
        </button>
      </div>
    </div>
  );

  const tableHeaders = ['Name','Email','Phone','Current Role','Company','Exp','Current CTC','Expected CTC','Notice','Location','Skills','Resume','Status','Actions'];

  return (
    <div className="min-h-screen w-full bg-slate-50 flex flex-col font-sans">

      {/* Modals */}
      {modal && (
        <StatusUpdateModal mode={modal.mode} candidateName={modal.candidateName} count={modal.count}
          onConfirm={handleModalConfirm} onClose={() => setModal(null)} submitting={submitting} />
      )}
      {interviewModal && (
        <InterviewSchedulingModal
          candidate={interviewModal.candidate}
          token={token}
          onClose={() => setInterviewModal(null)}
          onSuccess={validateAndFetchData}
        />
      )}
      {detailCandidate && (
        <CandidateDetailModal candidate={detailCandidate} onClose={() => setDetailCandidate(null)} />
      )}

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 w-full">
        <div className="w-full px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Building2 size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base font-bold text-slate-900 leading-tight">Candidate Review Portal</h1>
              {linkData && (
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-0.5">
                  {linkData.jobId !== 'N/A' && <span className="flex items-center gap-1"><Briefcase size={12} /> Job: <b className="text-slate-700">{linkData.jobId}</b></span>}
                  {linkData.expiresAt && <span className="flex items-center gap-1"><Clock size={12} /> Expires: <b className="text-slate-700">{formatDate(linkData.expiresAt)}</b></span>}
                  <span className="flex items-center gap-1"><Eye size={12} /> {linkData.accessCount} views</span>
                </div>
              )}
            </div>
          </div>
          <span className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-semibold flex-shrink-0">{candidates.length} candidates</span>
        </div>
      </header>

      {/* Bulk Actions Bar */}
      {selectedCandidates.length > 0 && (
        <div className="sticky top-[65px] z-10 bg-indigo-600 text-white shadow-lg w-full">
          <div className="w-full px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm font-semibold"><CheckSquare size={16} /> {selectedCandidates.length} Selected</div>
            <div className="flex items-center gap-2">
              <button onClick={openBulkModal} disabled={submitting}
                className="flex items-center gap-1.5 px-4 py-2 bg-white text-indigo-700 rounded-lg text-sm font-semibold hover:bg-indigo-50 disabled:opacity-50 shadow-sm">
                <Users size={15} /> Bulk Update Status
              </button>
              <button onClick={() => setSelectedCandidates([])}
                className="flex items-center gap-1 px-3 py-2 bg-indigo-500 hover:bg-indigo-400 rounded-lg text-sm font-medium">
                <X size={14} /> Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-auto w-full">
        <div className="w-full px-6 py-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 flex items-start gap-3">
            <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"><AlertCircle size={14} className="text-blue-600" /></div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Select candidates using checkboxes for <b>Bulk Update Status</b>, or click <b>Update</b> in Actions for a single candidate.
              Once slots are assigned, use <b>Schedule Meet</b> to create a Google Meet / Teams link and send it to the candidate.
            </p>
          </div>

          {candidates.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
              <AlertCircle size={40} className="text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-slate-700 mb-1">No Candidates</h3>
              <p className="text-sm text-slate-400">There are no candidates available in this link.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 sticky left-0 bg-slate-50 z-10 w-10">
                        <input type="checkbox"
                          checked={selectedCandidates.length === candidates.length && candidates.length > 0}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" />
                      </th>
                      {tableHeaders.map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {candidates.map((candidate) => (
                      <tr key={candidate.id} className={`transition-colors hover:bg-slate-50 ${selectedCandidates.includes(candidate.id) ? 'bg-indigo-50/60' : ''}`}>
                        <td className="px-4 py-3 sticky left-0 bg-inherit z-10">
                          <input type="checkbox" checked={selectedCandidates.includes(candidate.id)} onChange={() => toggleSelect(candidate.id)}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-semibold text-slate-900">{candidate.firstName} {candidate.lastName}</div>
                          <div className="text-xs text-slate-400">{candidate.applicationId || candidate.id}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-slate-700"><Mail size={12} className="text-slate-400" />
                            <span className="truncate max-w-[160px]">{candidate.primaryEmail}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-slate-700"><Phone size={12} className="text-slate-400" />{candidate.primaryPhone || 'N/A'}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{candidate.currentJobTitle || 'N/A'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{candidate.currentCompany || 'N/A'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{candidate.totalYearsOfExperience || '0'} yrs</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{candidate.currentCTC  ? `₹${Number(candidate.currentCTC).toLocaleString('en-IN')}` : 'N/A'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{candidate.expectedCTC ? `₹${Number(candidate.expectedCTC).toLocaleString('en-IN')}` : 'N/A'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{candidate.noticePeriod || 'N/A'}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{[candidate.city, candidate.country].filter(Boolean).join(', ') || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1 max-w-[180px]">
                            {candidate.technicalSkills?.length > 0 ? (
                              <>
                                {candidate.technicalSkills.slice(0, 3).map((skill, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded whitespace-nowrap">
                                    {typeof skill === 'object' ? skill.skillName : skill}
                                  </span>
                                ))}
                                {candidate.technicalSkills.length > 3 && (
                                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">+{candidate.technicalSkills.length - 3}</span>
                                )}
                              </>
                            ) : <span className="text-xs text-slate-400">N/A</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap"><ResumeBadge resumeUrl={candidate.resumeUrl} /></td>
                        <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={candidate.status} /></td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button onClick={() => setDetailCandidate(candidate)}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors">
                              <Eye size={12} /> View
                            </button>
                            <button onClick={() => openSingleModal(candidate)}
                              className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium flex items-center gap-1 transition-colors">
                              <UserCheck size={12} /> Update
                            </button>
                            {/* {canScheduleMeet(candidate) && (
                              <button
                                onClick={() => setInterviewModal({ candidate })}
                                className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap transition-colors">
                                <Video size={12} /> Schedule Meet
                              </button>
                            )} */}
                            {candidate.meetLink && (
                              <a href={candidate.meetLink} target="_blank" rel="noopener noreferrer"
                                className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap transition-colors">
                                <Video size={12} /> Join Meet
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 flex-shrink-0 w-full">
        <div className="w-full px-6 py-4 flex items-center justify-between text-xs text-slate-400">
          <span>Secure candidate review link — your selections are sent to the recruiter.</span>
          {linkData?.expiresAt && <span>Expires: <b className="text-slate-600">{formatDate(linkData.expiresAt)}</b></span>}
        </div>
      </footer>
    </div>
  );
};

export default ClientAccessPage;